import { useState, useCallback, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-client';
import type { SearchResult } from '@/lib/types';

const DEBOUNCE_DELAY = 300;

export function useSearch(initialQuery: string = '') {
  const [query, setQuery] = useState(initialQuery);
  const [debouncedQuery, setDebouncedQuery] = useState(initialQuery);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setDebouncedQuery(query);
    }, DEBOUNCE_DELAY);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [query]);

  const searchQuery = useQuery({
    queryKey: queryKeys.search.results(debouncedQuery),
    queryFn: async (): Promise<SearchResult> => {
      if (!debouncedQuery.trim()) {
        return { assets: [], inspections: [], deployments: [] };
      }
      const response = await apiClient.get('/api/v1/search/', {
        params: { q: debouncedQuery },
      });
      const data = response.data;
      // Normalize: API may return { query, results: { assets?, ... } } or { assets, inspections, deployments }
      if (data.results && !Array.isArray(data.results)) {
        return {
          assets: data.results.assets ?? [],
          inspections: data.results.inspections ?? [],
          deployments: data.results.deployments ?? [],
        };
      }
      return {
        assets: data.assets ?? [],
        inspections: data.inspections ?? [],
        deployments: data.deployments ?? [],
      };
    },
    enabled: debouncedQuery.trim().length > 0,
    staleTime: 1000 * 60,
  });

  const clearSearch = useCallback(() => {
    setQuery('');
    setDebouncedQuery('');
  }, []);

  const hasResults = searchQuery.data && (
    (searchQuery.data.assets?.length ?? 0) > 0 ||
    (searchQuery.data.inspections?.length ?? 0) > 0 ||
    (searchQuery.data.deployments?.length ?? 0) > 0
  );

  const totalResults = searchQuery.data
    ? (searchQuery.data.assets?.length ?? 0) +
      (searchQuery.data.inspections?.length ?? 0) +
      (searchQuery.data.deployments?.length ?? 0)
    : 0;

  return {
    query,
    setQuery,
    debouncedQuery,
    results: searchQuery.data,
    isLoading: searchQuery.isLoading,
    isError: searchQuery.isError,
    error: searchQuery.error,
    clearSearch,
    hasResults,
    totalResults,
  };
}

export interface SearchNavigationState {
  selectedIndex: number;
  selectedType: 'assets' | 'inspections' | 'deployments' | null;
}

export function useSearchNavigation(results: SearchResult | undefined) {
  const [state, setState] = useState<SearchNavigationState>({
    selectedIndex: -1,
    selectedType: null,
  });

  const flattenedResults = useCallback(() => {
    if (!results) return [];
    
    const items: Array<{
      type: 'assets' | 'inspections' | 'deployments';
      index: number;
      id: number;
    }> = [];

    (results.assets ?? []).forEach((_, index) => {
      items.push({ type: 'assets', index, id: results.assets[index].id });
    });
    (results.inspections ?? []).forEach((_, index) => {
      items.push({ type: 'inspections', index, id: results.inspections[index].id });
    });
    (results.deployments ?? []).forEach((_, index) => {
      items.push({ type: 'deployments', index, id: results.deployments[index].id });
    });

    return items;
  }, [results]);

  const moveUp = useCallback(() => {
    const items = flattenedResults();
    if (items.length === 0) return;

    setState((prev) => {
      const currentFlatIndex = items.findIndex(
        (item) => item.type === prev.selectedType && item.index === prev.selectedIndex
      );
      
      if (currentFlatIndex <= 0) {
        const lastItem = items[items.length - 1];
        return { selectedIndex: lastItem.index, selectedType: lastItem.type };
      }
      
      const prevItem = items[currentFlatIndex - 1];
      return { selectedIndex: prevItem.index, selectedType: prevItem.type };
    });
  }, [flattenedResults]);

  const moveDown = useCallback(() => {
    const items = flattenedResults();
    if (items.length === 0) return;

    setState((prev) => {
      const currentFlatIndex = items.findIndex(
        (item) => item.type === prev.selectedType && item.index === prev.selectedIndex
      );
      
      if (currentFlatIndex === -1 || currentFlatIndex >= items.length - 1) {
        const firstItem = items[0];
        return { selectedIndex: firstItem.index, selectedType: firstItem.type };
      }
      
      const nextItem = items[currentFlatIndex + 1];
      return { selectedIndex: nextItem.index, selectedType: nextItem.type };
    });
  }, [flattenedResults]);

  const getSelectedItem = useCallback(() => {
    if (!results || state.selectedType === null || state.selectedIndex === -1) {
      return null;
    }

    const typeResults = results[state.selectedType];
    if (state.selectedIndex >= typeResults.length) {
      return null;
    }

    return {
      type: state.selectedType,
      item: typeResults[state.selectedIndex],
    };
  }, [results, state]);

  const reset = useCallback(() => {
    setState({ selectedIndex: -1, selectedType: null });
  }, []);

  const isSelected = useCallback(
    (type: 'assets' | 'inspections' | 'deployments', index: number) => {
      return state.selectedType === type && state.selectedIndex === index;
    },
    [state]
  );

  return {
    selectedIndex: state.selectedIndex,
    selectedType: state.selectedType,
    moveUp,
    moveDown,
    getSelectedItem,
    reset,
    isSelected,
  };
}
